import { type ComponentType, lazy } from 'react';
import { Button } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

/**
 * Enhanced lazy loading with automatic retry on chunk load failure
 * Handles the case where a deployment happens while the user has the app open
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retries: number = 3,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const attemptImport = async (attempt: number): Promise<{ default: T }> => {
      try {
        return await componentImport();
      } catch (error) {
        // Check if it's a chunk loading error
        // Chrome: "Failed to fetch dynamically imported module", Firefox:
        // "error loading dynamically imported module", Safari: "Importing a
        // module script failed."
        const isChunkError =
          error instanceof Error &&
          (error.name === 'ChunkLoadError' ||
            error.message?.includes('Loading chunk') ||
            error.message?.includes('Failed to fetch') ||
            error.message?.includes('error loading dynamically imported module') ||
            error.message?.includes('Importing a module script failed'));

        if (isChunkError && attempt < retries) {
          console.warn(`Chunk load failed (attempt ${attempt + 1}/${retries}), retrying...`);

          // Wait a bit before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));

          return attemptImport(attempt + 1);
        }

        // If it's a chunk error and we've exhausted retries, show toast notification
        if (isChunkError) {
          console.error('Chunk loading failed after retries. A new version may be available.');

          enqueueSnackbar('Unable to load this page. A new version may be available.', {
            variant: 'error',
            persist: true,
            action: (
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                Refresh
              </Button>
            ),
          });
        }

        throw error;
      }
    };

    return attemptImport(0);
  });
}
